"use client";

import { useState, useRef, useEffect, useId } from "react";
import { Link as LinkIcon, Check } from "@phosphor-icons/react";
import MaskIcon from "@/components/ui/MaskIcon";

/**
 * Share an article or guide (`article-contents-rail-spec.md` §6b.1).
 *
 * ⛔ NOT `ShareButton`. That one is the professional profile's QR share-CARD
 * modal — it rasterises a branded PNG. This is four links.
 *
 * ⚠️ The URL must be the PRODUCTION one, passed in by the server, never
 * `window.location.href`. Editors and reviewers open these pages on localhost
 * and on Netlify deploy previews, and a shared link from either is dead — the
 * same trap `getPublicProfileUrl` exists for on the share card.
 */
export default function ShareRow({
  url,
  title,
  labels,
  heading,
  className,
}: {
  url: string;
  title: string;
  labels: { linkedin: string; facebook: string; threads: string; copy: string; copied: string };
  /**
   * Visible label, which also turns the row into a ruled bar (owner sketch,
   * 2026-09-09). Passed only where the row sits UNDER the article body — there
   * it interrupts a column of prose and needs to say what it is, and the rules
   * are what close the article off. In the rail it is the first thing in a
   * column of labelled blocks and reads fine as four icons.
   */
  heading?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const headingId = useId();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);

  // ⭐ THE SAME BRAND MARKS THE FOOTER USES (owner, 2026-09-09), painted through
  // `MaskIcon` rather than Phosphor's logo set — two share surfaces on one site
  // drawing the same three networks in two different glyph styles is the kind
  // of drift nobody reports and everybody notices. `size` is per icon because
  // each source file fills its own viewBox differently. MEASURED with
  // `getBBox()` on the inlined source rather than guessed: the glyph fills 69%
  // of Facebook's viewBox height, 72% of LinkedIn's and 99.8% of Threads', so
  // one shared size would have drawn Threads a third larger than its
  // neighbours. These three land at 13.1 / 12.9 / 13.0 px of actual ink, which
  // is the ~13px the footer's row was already calibrated to.
  const targets = [
    { key: "linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, icon: "/icon_linkedin.svg", size: 18, label: labels.linkedin },
    { key: "facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, icon: "/icon_facebook.svg", size: 19, label: labels.facebook },
    { key: "threads", href: `https://www.threads.net/intent/post?text=${t}%20${u}`, icon: "/icon_threads.svg", size: 13, label: labels.threads },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard (insecure context, embedded preview). Silent by design —
      // the same posture as CopyButton; there is nothing useful to tell the
      // reader and the other three actions still work.
    }
  };

  const cls =
    "group/share grid h-34 w-34 place-items-center rounded-control border border-card-line text-gray-500 transition-colors hover:border-navy hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold";

  const icons = (
    <div className="flex gap-8">
      {targets.map(({ key, href, icon, size, label }) => (
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={cls}>
          <MaskIcon src={icon} size={size} className="bg-gray-500 transition-colors group-hover/share:bg-navy" />
        </a>
      ))}
      <button type="button" onClick={copy} aria-label={copied ? labels.copied : labels.copy} className={cls}>
        {copied ? (
          <Check size={15} weight="bold" aria-hidden="true" className="text-gold-ink" />
        ) : (
          <LinkIcon size={15} weight="bold" aria-hidden="true" />
        )}
      </button>
      {/* A renamed accessible name is not reliably re-announced on a focused
          button, so the state change gets its own live region. Same fix as
          CopyButton. */}
      <span aria-live="polite" className="sr-only">
        {copied ? labels.copied : ""}
      </span>
    </div>
  );

  if (!heading) return <div className={className}>{icons}</div>;

  // ⚠️ `role="group"` + `aria-labelledby`, so the visible label names the four
  // controls it sits beside instead of being loose text next to them. Each link
  // already carries its own `aria-label`, so nothing was unnamed — this is what
  // makes "Share article" the heading OF that set rather than a caption near
  // it. `useId` because the component renders more than once per page.
  return (
    <div
      role="group"
      aria-labelledby={headingId}
      className={`flex items-center justify-between gap-13 border-y border-card-line py-13 ${className ?? ""}`}
    >
      <span id={headingId} className="text-2xs font-semibold uppercase tracking-widest text-navy">
        {heading}
      </span>
      {icons}
    </div>
  );
}
